/**
 * MODULE: InsertFieldDropdown
 * PURPOSE: Searchable dropdown for inserting CRM fields into email templates
 * DEPENDENCIES: flowbite-react, contactFieldsService
 * AGENT SAFETY: 🟢 SAFE
 */

import React, { useState, useMemo } from 'react';
import { Dropdown, TextInput, Badge, DropdownItem } from 'flowbite-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ContactFieldsService, type ContactField, type FieldCategory } from '../../services/contactFieldsService';

interface InsertFieldDropdownProps {
  onFieldSelect: (field: ContactField) => void;
  buttonLabel?: string;
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg';
  buttonColor?: 'gray' | 'purple';
}

const FIELD_CATEGORIES = ContactFieldsService.getAllFieldCategories();

function getCategoryBadgeColor(categoryId: string): 'warning' | 'info' | 'purple' | 'success' | 'gray' {
  const map: Record<string, 'warning' | 'info' | 'purple' | 'success' | 'gray'> = {
    contact: 'warning',
    company: 'info',
    deal: 'purple',
    sender: 'success',
  };
  return map[categoryId] ?? 'gray';
}

export default function InsertFieldDropdown({
  onFieldSelect,
  buttonLabel = 'Insert Field',
  buttonSize = 'sm',
  buttonColor = 'gray',
}: InsertFieldDropdownProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return FIELD_CATEGORIES;
    const lower = searchQuery.toLowerCase();
    return FIELD_CATEGORIES.map((cat: FieldCategory) => ({
      ...cat,
      fields: cat.fields.filter(
        (f) =>
          f.label.toLowerCase().includes(lower) ||
          f.placeholder.toLowerCase().includes(lower)
      ),
    })).filter((cat) => cat.fields.length > 0);
  }, [searchQuery]);

  const totalFields = filteredCategories.reduce((sum, cat) => sum + cat.fields.length, 0);

  const triggerSizeClass =
    {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-2.5 text-lg',
    }[buttonSize] ?? 'px-3 py-1.5 text-sm';

  const handleSelect = (field: ContactField) => {
    onFieldSelect(field);
    setSearchQuery('');
  };

  return (
    <Dropdown
      label=""
      dismissOnClick={false}
      renderTrigger={() => (
        <button
          type="button"
          className={`inline-flex items-center gap-2 rounded-lg border font-medium transition-colors ${triggerSizeClass} ${
            buttonColor === 'purple'
              ? 'border-purple-600 bg-purple-600 text-white hover:bg-purple-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
          }`}
        >
          <span>📋</span>
          <span>{buttonLabel}</span>
        </button>
      )}
    >
      <div
        className="p-2 border-b dark:border-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <TextInput
            type="search"
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sizing="sm"
            className="w-64 pl-9"
          />
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredCategories.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No fields found matching &quot;{searchQuery}&quot;
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.id}>
              <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </div>
              {category.fields.map((field) => (
                <DropdownItem
                  key={field.id}
                  onClick={() => handleSelect(field)}
                  className="group"
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {field.label}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        Example: {field.example}
                      </div>
                    </div>
                    <Badge
                      color={getCategoryBadgeColor(category.id)}
                      size="sm"
                      className="shrink-0"
                    >
                      {field.placeholder}
                    </Badge>
                  </div>
                </DropdownItem>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="border-t p-2 text-xs text-gray-500 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <span>{totalFields} fields available</span>
          <span className="text-purple-600 dark:text-purple-400">Click to insert</span>
        </div>
      </div>
    </Dropdown>
  );
}
